import { environment } from '../config/environment';
import { useApiClient } from './api';
import { ImportJobStatus, Platform, PlatformDetails, PlatformImporter, Integration, IntegrationTokenAllocation } from './integration.types';

export const useIntegrationService = () => {
    const apiClient = useApiClient();

    const getAllPlatforms = async () => {
        try {
            const response = await apiClient.get<Platform[]>(`${environment.ctLiteApiUrl}/integration/list`);
            return response.data;
        } catch (error) {
            console.error('Error getting platforms:', error);
            throw error;
        }
    };

    const getPlatformDetails = async (name: string, type: string) => {
        try {
            const response = await apiClient.get<PlatformDetails>(
                `${environment.ctLiteApiUrl}/integration/details?platformName=${name}&platformType=${type}`
            );
            return response.data;
        } catch (error) {
            console.error('Error getting platform details:', error);
            throw error;
        }
    };

    const getPlatformImporters = async (name: string, locale: string) => {
        try {
            const response = await apiClient.get<PlatformImporter[]>(
                `${environment.ctLiteApiUrl}/integration/import/list-full?platformName=${name}&locale=${locale}`
            );
            return response.data;
        } catch (error) {
            console.error('Error getting platform importers:', error);
            throw error;
        }
    };

    const getIntegrationsOverview = async (disableCache: boolean = true): Promise<Integration[]> => {
        try {
            const url = `${environment.ctLiteApiUrl}/integration/overview${disableCache ? '?t=' + Date.now() : ''}`;
            const response = await apiClient.get<any>(url);
            return response.data;
        } catch (error) {
            console.error('Error getting integrations overview:', error);
            throw error;
        }
    };

    const deleteIntegration = async (name: string, jobId: number, jobTableName: string) => {
        try {
            const params = new URLSearchParams();
            params.append('name', name);
            if (jobId) params.append('jobId', jobId?.toString());
            if (jobTableName) params.append('jobTableName', jobTableName);

            await apiClient.post(`${environment.ctLiteApiUrl}/integration/delete?${params.toString()}`, '');
        } catch (error) {
            console.error('Error deleting integration:', error);
            throw error;
        }
    };

    const getTokenAllocation = async (name: string): Promise<{ [key: string]: IntegrationTokenAllocation[] }> => {
        try {
            const params = new URLSearchParams();
            params.append('name', name);
            params.append('t', Date.now().toString());

            const response = await apiClient.get(
                `${environment.ctLiteApiUrl}/integration/token-allocation?${params.toString()}`
            );
            return response.data;
        } catch (error) {
            console.error('Error getting token allocation:', error);
            throw error;
        }
    };

    const getJobsStatus = async (filterByStatus: ImportJobStatus | null = null) => {
        try {
            const response = await apiClient.post(
                `${environment.ctLiteApiUrl}/integration/status${
                    filterByStatus === null ? '' : '?status=' + filterByStatus
                }`
            );
            return response.data;
        } catch (error) {
            console.error('Error getting jobs status:', error);
            throw error;
        }
    };

    const createExchangeIntegration = async (path: string, params: Record<string, any>) => {
        try {
            const body = {
                ...params,
                path,
            };

            const response = await apiClient.post(`${environment.ctLiteApiUrl}/integration/create-exchange`, body);
            return response.data;
        } catch (error) {
            console.error('Error creating job:', error);
            throw error;
        }
    };

    const createWalletIntegration = async (params: Record<string, any>) => {
        try {
            const response = await apiClient.post(`${environment.ctLiteApiUrl}/integration/create-wallet`, params);
            return response.data;
        } catch (error) {
            console.error('Error creating job:', error);
            throw error;
        }
    };

    const syncIntegration = async (params: Record<string, any>) => {
        try {
            const response = await apiClient.post(`${environment.ctLiteApiUrl}/integration/sync`, params);
            return response.data;
        } catch (error) {
            console.error('Error creating job:', error);
            throw error;
        }
    };

    const importCsv = async (
        importerUri: string,
        file: File,
        params: Record<string, any>,
        onProgress: (percentCompleted: number) => void
    ) => {
        try {
            const formData = new FormData();
            formData.append('importUri', importerUri);
            formData.append('datei', file, file.name);

            Object.entries(params).forEach(([key, value]) => {
                formData.append(key, value);
            });

            const response = await apiClient.post(`${environment.ctLiteApiUrl}/integration/import-csv`, formData, {
                headers: { 'Content-Type': 'multipart/form-data' },
                onUploadProgress: (progressEvent) => {
                    if (!progressEvent.total) return;
                    const percentCompleted = Math.round((progressEvent.loaded * 100) / progressEvent.total);
                    onProgress(percentCompleted);
                },
            });
            return response.data;
        } catch (error) {
            console.error('Error importing CSV:', error);
            throw error;
        }
    };

    const getIpAssignment = async (ipAssignmentName: string) => {
        try {
            const response = await apiClient.get(
                `${environment.ctLiteApiUrl}/integration/get-ip-assignment?ipAssignmentName=${ipAssignmentName}`
            );
            return response.data;
        } catch (error) {
            console.error('Error getting IP assignment:', error);
            throw error;
        }
    };

    return {
        getAllPlatforms,
        getPlatformDetails,
        getPlatformImporters,
        getIntegrationsOverview,
        deleteIntegration,
        getTokenAllocation,
        getJobsStatus,
        createExchangeIntegration,
        createWalletIntegration,
        syncIntegration,
        importCsv,
        getIpAssignment,
    };
};
